import { ChangeEvent, FC, useEffect, useState } from 'react';
import {
  Label,
  Typography,
  Stack,
  DatePicker,
  Input,
  useWindowSize,
  Icon,
  ButtonIcon,
  useDebounceCallback,
} from '@data-products-and-ai/react-components';

import { MOBILE_WIDTH, TABLET_WIDTH } from '@/constants';
import { OutagesFiltersI } from '@/types';
import { SitesFilter } from '@/pages/Outages/components';

import '@/pages/Outages/components/OutagesFilters/OutagesFilters.scss';

interface OutagesFiltersProps {
  disabled?: boolean;
  outagesFilters: OutagesFiltersI;
  onFiltersUpdate: (filters: Partial<OutagesFiltersI>) => void;
}

export const OutagesFilters: FC<OutagesFiltersProps> = ({
  disabled = false,
  outagesFilters,
  onFiltersUpdate,
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { width } = useWindowSize();
  const isSmallScreen = width <= TABLET_WIDTH;
  const startDate = outagesFilters.startDate
    ? new Date(outagesFilters.startDate).getTime() / 1000
    : undefined;
  const endDate = outagesFilters.endDate
    ? new Date(outagesFilters.endDate).getTime() / 1000
    : undefined;

  const handleFiltersOpen = () => {
    setIsFiltersOpen(!isFiltersOpen);
  };

  const debouncedSetQuery = useDebounceCallback((value: string) => {
    onFiltersUpdate({ query: value });
  }, 300);

  const handleQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    debouncedSetQuery(event.target.value);
  };

  const handleStartDateChange = (date: number | null) => {
    if (!date) {
      onFiltersUpdate({ startDate: null });
      return;
    }
    const formattedDate = new Date(date * 1000);
    const midnightUTC =
      Date.UTC(
        formattedDate.getUTCFullYear(),
        formattedDate.getUTCMonth(),
        formattedDate.getUTCDate(),
      ) / 1000;
    const localMidnight = new Date(midnightUTC * 1000).toISOString();

    onFiltersUpdate({ startDate: localMidnight });
  };

  const handleEndDateChange = (date: number | null) => {
    if (!date) {
      onFiltersUpdate({ endDate: null });
      return;
    }
    const formattedDate = new Date(date * 1000);
    const midnightUTC =
      Date.UTC(
        formattedDate.getUTCFullYear(),
        formattedDate.getUTCMonth(),
        formattedDate.getUTCDate(),
      ) / 1000;
    const localMidnight = new Date(midnightUTC * 1000).toISOString();

    onFiltersUpdate({ endDate: localMidnight });
  };

  const handleSiteChange = (siteId: string) => {
    onFiltersUpdate({ siteId });
  };

  useEffect(() => {
    if (!isSmallScreen && !isFiltersOpen) {
      setIsFiltersOpen(true);
    }
  }, [isSmallScreen, isFiltersOpen]);

  return (
    <div className="outages-filters">
      {isSmallScreen && (
        <div className="outages-filters__button">
          <ButtonIcon
            hoverType="background"
            icon="IconFilter"
            onClick={handleFiltersOpen}
          />
        </div>
      )}
      {isFiltersOpen && (
        <div className="outages-filters__content">
          <Stack verticalAlign="top">
            <Label>
              <Typography color="var(--color-neutral)" tag="textsmall">
                Site Name
              </Typography>
            </Label>
            <SitesFilter disabled={disabled} onChange={handleSiteChange} />
          </Stack>

          <Stack verticalAlign="top">
            <Label>
              <Typography color="var(--color-neutral)" tag="textsmall">
                Start Date
              </Typography>
            </Label>
            <DatePicker
              id="start-date"
              dateFormat="MM/DD/YYYY"
              highlightToday
              allowTextEdit
              defaultValue={startDate}
              onChange={handleStartDateChange}
            />
          </Stack>

          <Stack verticalAlign="top">
            <Label>
              <Typography color="var(--color-neutral)" tag="textsmall">
                End Date
              </Typography>
            </Label>
            <DatePicker
              id="end-date"
              dateFormat="MM/DD/YYYY"
              highlightToday
              allowTextEdit
              defaultValue={endDate}
              onChange={handleEndDateChange}
            />
          </Stack>

          <Stack verticalAlign="top">
            <Label>
              <Typography color="var(--color-neutral)" tag="textsmall">
                Search
              </Typography>
            </Label>
            <div className="outages-filters__search">
              <Input
                id="search"
                defaultValue={outagesFilters.query}
                placeholder="Search by text..."
                onChange={handleQueryChange}
              />
            </div>
          </Stack>
        </div>
      )}
      {isFiltersOpen && width <= MOBILE_WIDTH && (
        <Stack inline verticalAlign="center">
          <Icon
            iconSize={1}
            color="var(--color-default)"
            icon="IconCaretDown"
          />
        </Stack>
      )}
    </div>
  );
};
